// テキスト描画関連
import { layoutData, currentPageSize, updateTextValues, setLayoutData, setCurrentPageSize } from "./state.js";
import { qToPixels, mmToPixels, wrapText, waitForFonts, getFormTextValues } from "./utils.js";

// 単一テキスト要素を描画する関数
export const drawTextElement = (ctx, elementConfig, text, scale) => {
  if (!text || !elementConfig) return;

  // 座標とサイズをピクセルに変換
  const x = mmToPixels(elementConfig.position.x, scale);
  const y = mmToPixels(elementConfig.position.y, scale);
  const width = mmToPixels(elementConfig.width, scale);
  const height = mmToPixels(elementConfig.height, scale);
  const fontSize = qToPixels(elementConfig.fontSize, scale);
  const lineHeight = qToPixels(elementConfig.lineHeight, scale);

  // フォント設定
  ctx.font = `${fontSize}px "${elementConfig.fontFamily}", sans-serif`;
  ctx.fillStyle = elementConfig.color;
  ctx.textBaseline = "top";

  // テキストボックスの境界を描画（デバッグ用、titleとmessageのみ）
  const elementKey = Object.keys(layoutData[currentPageSize].textElements).find((key) => layoutData[currentPageSize].textElements[key] === elementConfig);
  if (elementKey === "title" || elementKey === "message") {
    ctx.strokeStyle = "rgba(255, 0, 0, 0.3)";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);
  }

  // 複数行対応の場合
  if (elementConfig.maxLines && elementConfig.maxLines > 1) {
    const lines = wrapText(ctx, text, width, elementConfig.maxLines);

    for (let i = 0; i < lines.length; i++) {
      let textX = x;
      let textY = y + i * lineHeight;

      // 垂直配置の調整（titleの下側張り付き）
      if (elementConfig.verticalAlign === "bottom") {
        const totalTextHeight = lines.length * lineHeight;
        textY = y + height - totalTextHeight + i * lineHeight;
      }

      // 空行の場合はスキップ
      if (lines[i] === "") {
        continue;
      }

      // 文字揃えの処理
      if (elementConfig.align === "center") {
        ctx.textAlign = "center";
        textX = x + width / 2;
      } else if (elementConfig.align === "justify" && i < lines.length - 1) {
        // 両端揃え（最後の行以外）
        ctx.textAlign = "left";
        const line = lines[i];
        if (line.length > 1) {
          const totalWidth = ctx.measureText(line).width;
          const spaceWidth = (width - totalWidth) / (line.length - 1);

          for (let j = 0; j < line.length; j++) {
            ctx.fillText(line[j], textX, textY);
            textX += ctx.measureText(line[j]).width + spaceWidth;
          }
          continue;
        }
      } else {
        ctx.textAlign = "left";
      }

      ctx.fillText(lines[i], textX, textY);
    }
  } else {
    // 単一行の場合
    let textX = x;
    let textY = y;

    if (elementConfig.align === "center") {
      ctx.textAlign = "center";
      textX = x + width / 2;
    } else {
      ctx.textAlign = "left";
    }

    ctx.fillText(text, textX, textY);
  }

  // textAlignをリセット
  ctx.textAlign = "left";
};

// 全テキスト要素を描画する関数
export const drawAllTextElements = (mainCtx, mainAlbumCanvas, currentSelectedTemplate) => {
  if (!mainCtx || !layoutData || !currentSelectedTemplate) return;

  // 現在のページサイズを取得
  const pageSize = currentSelectedTemplate.pageSize || "A4";
  setCurrentPageSize(pageSize);
  const layout = layoutData[pageSize];

  if (!layout) {
    console.error(`Layout data not found for page size: ${pageSize}`);
    return;
  }

  // フォーム入力値を取得
  const texts = getFormTextValues();

  // layout.jsonのページサイズを使用
  const pageWidthMM = layout.pageSize.width;
  const pageHeightMM = layout.pageSize.height;

  const canvasWidth = mainAlbumCanvas.width;
  const canvasHeight = mainAlbumCanvas.height;

  const scaleX = canvasWidth / pageWidthMM;
  const scaleY = canvasHeight / pageHeightMM;
  const scale = Math.min(scaleX, scaleY);

  const actualWidth = pageWidthMM * scale;
  const actualHeight = pageHeightMM * scale;
  const offsetX = (canvasWidth - actualWidth) / 2;
  const offsetY = (canvasHeight - actualHeight) / 2;

  // Canvas座標系の調整を考慮してテキストを描画
  mainCtx.save();
  mainCtx.translate(offsetX, offsetY);

  // 各テキスト要素を描画
  Object.keys(layout.textElements).forEach((elementKey) => {
    const elementConfig = layout.textElements[elementKey];
    const text = texts[elementKey];

    if (text) {
      drawTextElement(mainCtx, elementConfig, text, scale);
    }
  });

  mainCtx.restore();
};

// フォーム入力の変更を監視する関数
export const observeTextInputs = (drawAllTextElementsCallback) => {
  const inputIds = [
    "mase_case_entry_type_a1_itemStrVarchar01", // title
    "mase_case_entry_type_a1_itemStrText02", // message
  ];

  // hidden inputは変更されないので監視対象から除外
  // studentName, department, courseはhidden inputのため監視不要

  inputIds.forEach((inputId) => {
    const input = document.getElementById(inputId);
    if (input) {
      // リアルタイム更新
      input.addEventListener("input", async () => {
        if (drawAllTextElementsCallback) {
          // メインキャンバスが表示されている場合のみ再描画
          const albumPreviewModal = document.getElementById("albumPreviewModal");
          if (albumPreviewModal && albumPreviewModal.style.display !== "none") {
            await drawAllTextElementsCallback();
          }
        }
      });

      // フォーカス離脱時の更新
      input.addEventListener("blur", async () => {
        if (drawAllTextElementsCallback) {
          // メインキャンバスが表示されている場合のみ再描画
          const albumPreviewModal = document.getElementById("albumPreviewModal");
          if (albumPreviewModal && albumPreviewModal.style.display !== "none") {
            await drawAllTextElementsCallback();
          }
        }
      });
    }
  });
};

// layout.jsonを読み込み、状態を更新する関数
export const initializeLayoutData = async () => {
  try {
    const response = await fetch("./layout.json");
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    setLayoutData(data);
    console.log("Layout data loaded:", data);

    // フォントの読み込み完了を待つ
    await waitForFonts();

    return data;
  } catch (error) {
    console.error("Layout data loading failed:", error);
    return null;
  }
};
